import { useState } from "react";
import { Images } from "lucide-react";
import type { AdjustmentsData, AIModeState } from "../types";
import { curatedRoomPhotos, curatedSpacePhotos } from "../utils/imageCuration";
import { PhotosSection } from "./LocationPreview";

interface ImagesPreviewProps {
  state: AIModeState;
  liveAdjustments: AdjustmentsData | null;
  selectedSpaceId?: string | null;
}

export default function ImagesPreview({
  state,
  liveAdjustments,
  selectedSpaceId,
}: ImagesPreviewProps) {
  const allSpaces =
    liveAdjustments?.selectedSpaces ?? state.adjustments?.selectedSpaces ?? [];
  // Show only the space chosen via the "Pré-visualizar" button; falls back to
  // the first space so there is always something on screen.
  const effectiveId = selectedSpaceId ?? allSpaces[0]?.id;
  const spaces = allSpaces.filter((s) => s.id === effectiveId);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const onImageError = (url: string) =>
    setFailedImages((prev) => new Set(prev).add(url));

  const hasAnyPhoto = spaces.some(
    (s) => curatedRoomPhotos(s).length > 0 || curatedSpacePhotos(s).length > 0,
  );

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{ background: "#FDFBF7" }}
    >
      <div
        className="flex-none flex items-center gap-2 px-5 py-3"
        style={{ borderBottom: "1px solid #E8E6E3" }}
      >
        <Images className="w-4 h-4" style={{ color: "#8C7200" }} />
        <span className="text-sm font-semibold" style={{ color: "#1A1814" }}>
          Pré-visualização das imagens
        </span>
      </div>

      {hasAnyPhoto ? (
        <div
          className="flex-1 overflow-y-auto p-4 flex flex-col gap-6"
          style={{ scrollbarWidth: "none" }}
        >
          {spaces.map((space) => {
            const roomPhotos = curatedRoomPhotos(space);
            const spacePhotos = curatedSpacePhotos(space);
            if (roomPhotos.length === 0 && spacePhotos.length === 0)
              return null;
            return (
              <div key={space.id} className="grid grid-cols-2 gap-x-5 gap-y-6">
                {roomPhotos.length > 0 && (
                  <PhotosSection
                    photos={roomPhotos}
                    variant="sala"
                    categoryLabel="Sala privativa"
                    nameLabel={space.roomName}
                    failedImages={failedImages}
                    onImageError={onImageError}
                  />
                )}
                {spacePhotos.length > 0 && (
                  <PhotosSection
                    photos={spacePhotos}
                    variant="espaco"
                    categoryLabel="Espaço"
                    nameLabel={space.spaceName}
                    failedImages={failedImages}
                    onImageError={onImageError}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-center px-8">
          <p className="text-sm" style={{ color: "#9E9A94" }}>
            Nenhuma imagem selecionada.
          </p>
        </div>
      )}
    </div>
  );
}
